import React, { useCallback, useEffect, useState } from "react"
import styled from "styled-components"
import { Card, Divider, Typography, Checkbox, Alert, Skeleton } from "antd"
import { TPolicyRenewals } from "models/policies/policyRenewals"
import { IConfigurationContextPolicyDetails } from "contexts/configuration/context"
import { PolicyRenewalChangeConfirmation } from "../policy-renewal-change-confirmation"

const PolicyRenewalBox = styled.div`
  position: relative;
  padding-left: 65px;
`

const PolicyStyledSwitch = styled(Checkbox)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`

const { Title } = Typography

export const PolicyRenewals: React.FunctionComponent<IPolicyRenewals> = ({
  renewalsConfiguration,
  isSending,
  isError,
  sendRequest,
  values,
  policyPlanReferenceID,
  renewalsSettings = [],
  isLoading,
  setPolicyRenewalWithdraw,
  setPolicyRenewalReinstated,
  isPolicyRenewalsWithdrawn,
  refetchTimelineEvents
}) => {
  const [renewalValues, setRenewalValues] = useState<{
    requiresReview: boolean
    allowAutoBind: boolean
    doNotRenew: boolean
  }>(values)

  const [updatedRenewalValues, setUpdatedRenewalValues] = useState<{
    requiresReview: boolean
    allowAutoBind: boolean
    doNotRenew: boolean
  }>(values)

  const [updatedValueType, setUpdatedValueType] =
    useState<TPolicyRenewalUpdateType>()

  const [isUpdatingRenewalStatus, setIsUpdatingRenewalStatus] = useState(false)

  const [autoRenewalSettings = { supportsAutoBind: false }] =
    renewalsSettings &&
    renewalsSettings.filter(
      (plan) => plan.referenceID === policyPlanReferenceID
    )

  const [isConfirming, setIsConfirming] = useState<boolean>(false)

  const autoBindSettings = {
    isDisabled: isSending || isPolicyRenewalsWithdrawn,
    isMessage: isPolicyRenewalsWithdrawn
  }

  const withdrawSettings = {
    isDisabled: isSending
  }

  const manualSettings = {
    isDisabled:
      isSending || isPolicyRenewalsWithdrawn || renewalValues.doNotRenew,
    isMessage: renewalValues.doNotRenew || isPolicyRenewalsWithdrawn
  }

  const intentionSettings = {
    isDisabled: isSending || isPolicyRenewalsWithdrawn,
    isMessage: isPolicyRenewalsWithdrawn
  }

  const confirmRenewalStatusUpdate = async () => {
    setIsUpdatingRenewalStatus(true)
    if (updatedValueType === "withdraw") {
      !isPolicyRenewalsWithdrawn
        ? await setPolicyRenewalWithdraw()
        : await setPolicyRenewalReinstated()
    } else {
      await sendRequest(updatedRenewalValues)
    }
    setRenewalValues(updatedRenewalValues)
    setIsConfirming(false)
    setIsUpdatingRenewalStatus(false)
    refetchTimelineEvents()
  }

  const cancelRenewalStatusUpdate = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    setIsConfirming(false)
  }

  const generateConfirmationText = useCallback(() => {
    switch (updatedValueType) {
      case "requiresReview":
        return updatedRenewalValues.requiresReview
          ? "Are you sure you wish to set this policy to require manual review?"
          : "Are you sure you wish to remove the requirement for manual review?"
      case "allowAutoBind":
        return updatedRenewalValues.allowAutoBind
          ? "Are you sure you wish to set this policy to auto-renew?"
          : "Are you sure you wish to remove the auto-renewal setting?"
      case "doNotRenew":
        return updatedRenewalValues.doNotRenew
          ? "Are you sure you wish to set this policy to not renew?"
          : "Are you sure you wish to remove the no intention to renew setting?"
      case "withdraw":
        return !isPolicyRenewalsWithdrawn
          ? "Are you sure you wish to withdraw this policy?"
          : "Are you sure you wish to reinstate this policy?"
      default:
        return "Are you sure you wish to make this change?"
    }
  }, [updatedValueType, updatedRenewalValues, isPolicyRenewalsWithdrawn])

  useEffect(() => {
    setRenewalValues(values)
  }, [values])

  return (
    <>
      <Card title={"Options"}>
        <Skeleton loading={isLoading}>
          {isError && (
            <>
              <PolicyRenewalBox>
                <Alert
                  message={
                    renewalsConfiguration?.errorRenewal ||
                    "Something went wrong. please try again later"
                  }
                  type="error"
                />
              </PolicyRenewalBox>
              <Divider />
            </>
          )}
          {autoRenewalSettings.supportsAutoBind && (
            <PolicyRenewalBox>
              <PolicyStyledSwitch
                checked={renewalValues.allowAutoBind}
                disabled={autoBindSettings.isDisabled}
                onChange={(event) => {
                  setIsConfirming(true)
                  setUpdatedRenewalValues({
                    ...renewalValues,
                    allowAutoBind: event.target.checked,
                    doNotRenew: false
                  })
                  setUpdatedValueType("allowAutoBind")
                }}
              />
              <Title level={5}>
                {renewalsConfiguration?.autoRenewal?.title ||
                  "Auto-renew policy"}
              </Title>
              {renewalsConfiguration?.autoRenewal?.content ||
                "When set to true, this policies renewal quote will automatically bind and payment collected when the current policy expires."}
              {autoBindSettings.isMessage && (
                <Alert
                  style={{ marginTop: 10 }}
                  message={
                    renewalValues.doNotRenew
                      ? renewalsConfiguration?.autoRenewal?.disabledLabel
                      : renewalsConfiguration?.autoRenewal?.withdrawnLabel
                  }
                  type="info"
                />
              )}
            </PolicyRenewalBox>
          )}
          {autoRenewalSettings.supportsAutoBind && <Divider />}

          <PolicyRenewalBox>
            <PolicyStyledSwitch
              checked={renewalValues.requiresReview}
              disabled={manualSettings.isDisabled}
              onChange={(event) => {
                setIsConfirming(true)
                setUpdatedRenewalValues({
                  ...renewalValues,
                  requiresReview: event.target.checked
                })
                setUpdatedValueType("requiresReview")
              }}
            />
            <Title level={5}>
              {renewalsConfiguration?.manualRenewal?.title ||
                "Manual Renewal Review"}
            </Title>
            {renewalsConfiguration?.manualRenewal?.content ||
              "Marks the renewal quote for this policy as requiring manual review before being sent/made available to customer."}
            {manualSettings.isMessage && (
              <Alert
                style={{ marginTop: 10 }}
                message={
                  renewalValues.doNotRenew
                    ? renewalsConfiguration?.manualRenewal?.disabledLabel
                    : renewalsConfiguration?.manualRenewal?.withdrawnLabel
                }
                type="info"
              />
            )}
          </PolicyRenewalBox>
          <Divider />
          <PolicyRenewalBox>
            <PolicyStyledSwitch
              checked={isPolicyRenewalsWithdrawn}
              disabled={withdrawSettings.isDisabled}
              onChange={(event) => {
                setIsConfirming(true)
                setUpdatedValueType("withdraw")
              }}
            />
            <Title level={5}>
              {renewalsConfiguration?.withdrawRenewal?.title || "Withdraw"}
            </Title>
            {renewalsConfiguration?.withdrawRenewal?.content ||
              "When set to true, this option prevents the customer from renewing their policy."}
          </PolicyRenewalBox>

          <Divider />
          <PolicyRenewalBox>
            <PolicyStyledSwitch
              checked={renewalValues.doNotRenew}
              disabled={intentionSettings.isDisabled}
              onChange={(event) => {
                setIsConfirming(true)
                setUpdatedValueType("doNotRenew")
                setUpdatedRenewalValues({
                  doNotRenew: event.target.checked,
                  requiresReview: false,
                  allowAutoBind: false
                })
              }}
            />
            <Title level={5}>
              {renewalsConfiguration?.intentionRenewal?.title ||
                "No intention to renew"}
            </Title>
            {renewalsConfiguration?.intentionRenewal?.content ||
              `Tags the policy indicating the customer has no intention to renew. A renewal quote will not be assigned, and the customer will not receive any renewal pricing or follow-up notifications.`}
            {intentionSettings.isMessage && (
              <Alert
                style={{ marginTop: 10 }}
                message={
                  renewalsConfiguration?.autoRenewal?.withdrawnLabel ||
                  "Renewals have been withdrawn"
                }
                type="info"
              />
            )}
          </PolicyRenewalBox>
        </Skeleton>
      </Card>
      <PolicyRenewalChangeConfirmation
        isVisible={isConfirming}
        text={generateConfirmationText() ?? ""}
        onConfirm={confirmRenewalStatusUpdate}
        onCancel={cancelRenewalStatusUpdate}
        isLoading={isUpdatingRenewalStatus}
      />
    </>
  )
}

type TPolicyRenewalUpdateType =
  | "requiresReview"
  | "allowAutoBind"
  | "doNotRenew"
  | "withdraw"

export interface IPolicyRenewals {
  renewalsConfiguration?: IConfigurationContextPolicyDetails
  policyPlanReferenceID: string | undefined
  isError: boolean
  isLoading: boolean
  errorMessage?: string | undefined
  isSending: boolean
  isPolicyRenewalsWithdrawn: boolean
  sendRequest: (args: {
    requiresReview: boolean
    allowAutoBind: boolean
    doNotRenew: boolean
  }) => Promise<void>
  setPolicyRenewalWithdraw: () => Promise<void>
  setPolicyRenewalReinstated: () => Promise<void>
  renewalsSettings?: {
    referenceID: string
    supportsAutoBind: boolean
  }[]
  values: TPolicyRenewals
  refetchTimelineEvents: VoidFunction
}
